import React, { useState, useEffect, useRef } from 'react';
import StreamingEventsDisplay from './StreamingEventsDisplay';
import { streamDeepAnalysis, fetchAnalysisReport } from '../utils/api';

const AnalysisContainer = ({
    selectedProducts,
    onAnalysisComplete,
    onAnalysisError,
    isAnalysisLoading,
    setIsAnalysisLoading
}) => {
    const [streamingEvents, setStreamingEvents] = useState([]);
    const [analysisProgress, setAnalysisProgress] = useState('');
    const [hasStartedAnalysis, setHasStartedAnalysis] = useState(false);
    const [isAnalysisInProgress, setIsAnalysisInProgress] = useState(false);

    // Use ref to prevent double execution in React StrictMode
    const analysisStartedRef = useRef(false);

    // Start analysis when component mounts (only once)
    useEffect(() => {
        if (selectedProducts.length > 0 && !analysisStartedRef.current) {
            console.log('AnalysisContainer: Starting analysis for products:', selectedProducts.length);
            analysisStartedRef.current = true;
            setHasStartedAnalysis(true);
            setIsAnalysisInProgress(true);
            startAnalysis();
        }
    }, [selectedProducts]);

    const startAnalysis = async () => {
        if (selectedProducts.length === 0) {
            alert('Please select at least one product for deep analysis');
            return;
        }

        // Reset streaming events for fresh analysis
        setStreamingEvents([]);
        setAnalysisProgress('Initializing market analysis...');

        try {
            // Start streaming from backend (which now handles both analysis creation and streaming)
            console.log('Starting analysis streaming...');
            const streamResponse = await streamDeepAnalysis(selectedProducts);

            console.log('Stream response status:', streamResponse.status);
            console.log('Stream response headers:', Object.fromEntries(streamResponse.headers.entries()));

            if (!streamResponse.ok) {
                const errorText = await streamResponse.text();
                console.error('Stream response error:', errorText);
                throw new Error(`Failed to start analysis streaming: ${streamResponse.status} - ${errorText}`);
            }

            const reader = streamResponse.body.getReader();
            const decoder = new TextDecoder();

            // Step 3: Process streaming events
            let eventCount = 0;
            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    console.log('Stream ended, total events received:', eventCount);
                    break;
                }

                const chunk = decoder.decode(value);
                console.log('Raw chunk received:', chunk.substring(0, 200) + '...');

                const lines = chunk.split('\n');

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        eventCount++;
                        console.log(`Event #${eventCount}:`, line.substring(0, 100) + '...');
                        try {
                            const eventData = JSON.parse(line.slice(6));
                            console.log('Parsed event:', eventData);

                            // Add raw event to streaming events list
                            setStreamingEvents(prev => [...prev, {
                                id: Date.now() + Math.random(),
                                timestamp: new Date().toISOString(),
                                event: eventData,
                                raw: line
                            }]);

                            // Handle different event types from new backend for progress messages
                            if (eventData.event_type === 'analysis_started') {
                                setAnalysisProgress(eventData.data?.message || 'Starting market analysis...');
                            } else if (eventData.event_type === 'generate_query') {
                                setAnalysisProgress(eventData.data?.description || 'Generating search queries...');
                            } else if (eventData.event_type === 'web_research') {
                                const loopCount = eventData.data?.current_query ? 'additional' : 'initial';
                                setAnalysisProgress(`🔍 Researching: ${eventData.data?.current_query || 'market data'} (${eventData.data?.sources_count || 0} sources found)`);
                            } else if (eventData.event_type === 'consolidate_knowledge') {
                                setAnalysisProgress(`🧠 Synthesizing ${eventData.data?.research_loop_count || 0} research loops into comprehensive knowledge...`);
                            } else if (eventData.event_type === 'reflection') {
                                const loopNum = eventData.data?.research_loop_count || 0;
                                const isSufficient = eventData.data?.is_sufficient;
                                const status = isSufficient ? '✅ Sufficient' : '🔄 Needs more research';
                                setAnalysisProgress(`🤔 Analysis Loop ${loopNum}: ${status} - ${eventData.data?.assessment || 'Evaluating completeness'}`);
                            } else if (eventData.event_type === 'finalize_answer') {
                                setAnalysisProgress(eventData.data?.title || 'Finalizing comprehensive report...');
                            } else if (eventData.event_type === 'generate_html_report') {
                                setAnalysisProgress(eventData.data?.title || 'Generating HTML report...');
                            } else if (eventData.event_type === 'generate_json_report') {
                                setAnalysisProgress(eventData.data?.title || 'Generating JSON report...');
                            } else if (eventData.event_type === 'complete') {
                                // Analysis complete - button will be shown in StreamingEventsDisplay
                                console.log('Analysis streaming complete, waiting for user to click button');
                                return;
                            } else if (eventData.event_type === 'error') {
                                throw new Error(eventData.data?.message || 'Analysis failed');
                            } else {
                                // For any other event type, show detailed information
                                const title = eventData.data?.title;
                                const description = eventData.data?.description;
                                const thinking = eventData.data?.thinking_process;
                                const message = title || description || thinking || eventData.event_type;
                                setAnalysisProgress(message);
                            }
                        } catch (parseError) {
                            console.warn('Failed to parse streaming event:', line);
                        }
                    }
                }
            }

        } catch (error) {
            console.error('Deep analysis error:', error);
            onAnalysisError?.(error);
            setIsAnalysisLoading(false);
        }
        // Don't hide overlay in finally - let user click button to view results
    };

    const fetchFinalReport = async (analysisId) => {
        try {
            console.log('Fetching final report for analysis ID:', analysisId);
            const actualReport = await fetchAnalysisReport(analysisId);
            console.log('Extracted report data:', actualReport);

            // Store the final report in state
            onAnalysisComplete(actualReport);
            console.log('Report successfully set in state');

        } catch (error) {
            console.error('Failed to fetch final report:', error);
            onAnalysisError?.(error);
        }
    };

    // Return JSX directly as a React component
    return (
        <div className="analysis-fullscreen-layout">
            {/* Left side: Loading */}
            <div className="analysis-content-column">
                <div className="analysis-loading-container">
                    <h2>Analyzing Market Intelligence</h2>
                    <div className="loading-spinner-large"></div>
                    <p>{analysisProgress}</p>
                    <p className="analysis-note">
                        This may take a few minutes. Our AI is processing {selectedProducts.length} products and generating comprehensive market insights.
                    </p>
                </div>
            </div>

            {/* Right side: Streaming events */}
            <div className="streaming-events-column">
                {streamingEvents.length > 0 && (
                    <StreamingEventsDisplay
                        events={streamingEvents}
                        onAnalysisComplete={async () => {
                            // Find the analysis_id from the complete event
                            const completeEvent = streamingEvents.find(event =>
                                event.event.event_type === 'complete'
                            );
                            if (completeEvent && completeEvent.event.data?.analysis_id) {
                                await fetchFinalReport(completeEvent.event.data.analysis_id);
                                setIsAnalysisLoading(false);
                            } else {
                                alert('Analysis completed but could not find report. Please try again.');
                                setIsAnalysisLoading(false);
                            }
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default AnalysisContainer;
